import React from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { Box, Paper, Typography, List, ListItem, ListItemText } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useTranslation } from 'react-i18next';
import {
  CustomSettings,
  getDefaultSectionOrderForTemplateType,
  isInvoiceOrProformaTemplateType,
  isProformaInvoiceTemplateType,
} from 'subModule/src/services/invoice/types';
import { normalizeThermalSectionOrder } from './thermal/thermalInvoiceUtils';

interface LayoutCustomizerProps {
  settings: CustomSettings;
  onChange: (sectionOrder: string[]) => void;
  templateType?: number; // INVOICE = 2, JOBCARD = 3, WORKORDER = 4, INSPECTION = 5
  /** Invoice thermal: same drag-drop UX; omits vehicle; includes QR block section */
  isThermalInvoice?: boolean;
}

interface DraggableSectionProps {
  id: string;
  label: string;
  index: number;
  moveSection: (dragIndex: number, hoverIndex: number) => void;
}

const DraggableSection: React.FC<DraggableSectionProps> = ({ id, label, index, moveSection }) => {
  const { t } = useTranslation();
  const [{ isDragging }, drag] = useDrag({
    type: 'section',
    item: { id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: 'section',
    hover: (draggedItem: { id: string; index: number }) => {
      if (draggedItem.index !== index) {
        moveSection(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <ListItem
      ref={(node) => drag(drop(node))}
      sx={{
        border: '1px solid #ddd',
        borderRadius: 1,
        mb: 1,
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: '#f5f5f5',
        },
      }}
    >
      <DragIndicatorIcon sx={{ mr: 1, color: '#999' }} />
      <ListItemText primary={t(label)} />
    </ListItem>
  );
};

const LayoutCustomizer: React.FC<LayoutCustomizerProps> = ({
  settings,
  onChange,
  templateType = 2,
  isThermalInvoice = false,
}) => {
  const { t } = useTranslation();

  const getSectionOrder = () => {
    if (isThermalInvoice && isInvoiceOrProformaTemplateType(templateType)) {
      return normalizeThermalSectionOrder(settings.sectionOrder);
    }

    if (settings.sectionOrder && settings.sectionOrder.length > 0) {
      return settings.sectionOrder;
    }

    return getDefaultSectionOrderForTemplateType(templateType);
  };

  const sectionOrder = getSectionOrder();

  const sectionLabels: Record<string, string> = {
    logo: 'logo',
    header: 'companyCustomerDetails',
    vehicleInfo: 'vehicleInfo',
    qr: 'qrCodeSection',
    table: 'table',
    summary: 'summary',
    footer: 'footer',
    terms: 'termsAndConditions',
    notes: 'notesSection',
    inspectionItems: 'inspectionItems',
    vehicleImage: 'vehicleImage',
    customerSignature: 'customerSignature',
    inspectionRemarks: 'inspectionRemarks',
  };

  const getSectionLabel = (sectionId: string) => {
    if (
      sectionId === 'header' &&
      isProformaInvoiceTemplateType(templateType) &&
      settings.showCompanySection === false
    ) {
      return 'customerDetails';
    }
    return sectionLabels[sectionId] || sectionId;
  };

  const moveSection = (dragIndex: number, hoverIndex: number) => {
    const newOrder = [...sectionOrder];
    const [removed] = newOrder.splice(dragIndex, 1);
    newOrder.splice(hoverIndex, 0, removed);
    onChange(newOrder);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
        {t('dragToReorder')}
      </Typography>
      <Paper sx={{ p: 2 }}>
        <List>
          {sectionOrder.map((sectionId, index) => (
            <DraggableSection
              key={sectionId}
              id={sectionId}
              label={getSectionLabel(sectionId)}
              index={index}
              moveSection={moveSection}
            />
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default LayoutCustomizer;
